import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  Grid2,
  Stack,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import {
  formatVendorPaymentCsvUploadErrors,
  parseVendorPaymentCsvUploadError,
  VENDOR_PAYMENT_CSV_PAYMENT_MODE_VALUES,
} from "../../../../../models/PurchaseRequest/VendorPayments";
import { VendorPaymentsStore } from "../../../stores/VendorPaymentsStore";
import { useNotification } from "../../../../../utils/NotificationsContext";

interface VendorPaymentsCsvUploadDialogProps {
  open: boolean;
  onClose: () => void;
  store: VendorPaymentsStore;
}

export const VENDOR_PAYMENT_CSV_TEMPLATE_URL =
  "https://content.swageazy.com/templates/Sample_vendor_payments_template.csv";

const listItemSx = { mb: 0.5 };

const VendorPaymentsCsvUploadDialog: React.FC<
  VendorPaymentsCsvUploadDialogProps
> = ({ open, onClose, store }) => {
  const theme = useTheme();
  const { showSuccess, showError } = useNotification();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errorLines, setErrorLines] = useState<string[]>([]);

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setErrorLines([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [open]);

  const handleDownloadSample = () => {
    try {
      const link = document.createElement("a");
      link.href = VENDOR_PAYMENT_CSV_TEMPLATE_URL;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.download = "vendor_payments_template.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      showError("Error", "Failed to download vendor payments template.");
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setErrorLines(["Select a CSV file to upload."]);
      return;
    }
    setErrorLines([]);
    try {
      const result = await store.uploadVendorPaymentsCsv(selectedFile);
      const uploadErrors = formatVendorPaymentCsvUploadErrors(result?.errors);
      if (uploadErrors.length > 0) {
        setErrorLines(uploadErrors);
        return;
      }
      const count = Number(result?.created_count ?? 0);
      showSuccess(
        count > 0
          ? `Uploaded successfully. ${count} payment${count === 1 ? "" : "s"} created.`
          : "Upload completed.",
      );
      onClose();
    } catch (error) {
      setErrorLines(parseVendorPaymentCsvUploadError(error));
    }
  };

  const paymentModeText = VENDOR_PAYMENT_CSV_PAYMENT_MODE_VALUES.join(", ");

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogContent sx={{ p: 3 }}>
        <CustomTypography variant="heading4" fontWeight={700} sx={{ mb: 1 }}>
          Upload vendor payments CSV
        </CustomTypography>

        <CustomTypography
          variant="heading7"
          fontWeight={600}
          sx={{ mb: 0.75, mt: 1 }}
        >
          Required Fields
        </CustomTypography>
        <Box
          component="ul"
          sx={{
            m: 0,
            pl: 2.5,
            color: theme.palette.customColors.grayText,
          }}
        >
          <Box component="li" sx={listItemSx}>
            <CustomTypography variant="body2">
              <strong>PrID</strong> — Purchase request id
            </CustomTypography>
          </Box>
          <Box component="li" sx={listItemSx}>
            <CustomTypography variant="body2">
              <strong>PaymentDate</strong> — <strong>YYYY-MM-DD</strong>
            </CustomTypography>
          </Box>
          <Box component="li" sx={listItemSx}>
            <CustomTypography variant="body2">
              <strong>PaymentAmount</strong> — Greater than 0, maximum 2 decimal
              places
            </CustomTypography>
          </Box>
          <Box component="li" sx={listItemSx}>
            <CustomTypography variant="body2">
              <strong>UTRNo</strong> — Bank reference
            </CustomTypography>
          </Box>
          <Box component="li" sx={listItemSx}>
            <CustomTypography variant="body2">
              <strong>PaymentTiming</strong> —{" "}
              <strong> advance or post_invoice or post invoice </strong> only
            </CustomTypography>
          </Box>
        </Box>

        <CustomTypography
          variant="heading7"
          fontWeight={600}
          sx={{ mb: 0.75, mt: 1.5 }}
        >
          Optional Fields
        </CustomTypography>
        <Box
          component="ul"
          sx={{
            m: 0,
            pl: 2.5,
            color: theme.palette.customColors.grayText,
          }}
        >
          <Box component="li" sx={listItemSx}>
            <CustomTypography variant="body2">
                <strong>PaymentMode</strong> — use values such as: {paymentModeText}
            </CustomTypography>
          </Box>
          <Box component="li" sx={listItemSx}>
            <CustomTypography variant="body2">
              <strong>Remarks</strong>
            </CustomTypography>
          </Box>
        </Box>

        <CustomTypography
          variant="body2"
          color={theme.palette.customColors.grayText}
          sx={{ mt: 1.5, mb: 2 }}
        >
          Note: If any row is invalid, none of the data from the file will be
          saved.
        </CustomTypography>

        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
          <AppButton
            label="Download CSV template"
            variant="transparentPrimary"
            onClick={handleDownloadSample}
            customStyles={{ minHeight: 34 }}
          />
        </Stack>

        <Box
          sx={{
            border: `1px dashed ${theme.palette.customColors.lightGray}`,
            borderRadius: 2,
            p: 2,
            mb: 2,
            backgroundColor: theme.palette.customColors.veryLightGray,
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={(event) => {
              setSelectedFile(event.target.files?.[0] ?? null);
              setErrorLines([]);
            }}
            style={{ display: "none" }}
          />
          <AppButton
            label="Choose file"
            variant="transparentPrimary"
            onClick={() => fileInputRef.current?.click()}
            customStyles={{ minHeight: 34 }}
          />
          {selectedFile ? (
            <CustomTypography variant="body2" sx={{ mt: 1 }}>
              {selectedFile.name}
            </CustomTypography>
          ) : (
            <CustomTypography
              variant="body2"
              color={theme.palette.customColors.grayText}
              sx={{ mt: 1 }}
            >
              No file selected
            </CustomTypography>
          )}
        </Box>

        {errorLines.length > 0 ? (
          <Box
            sx={{
              mb: 2,
              maxHeight: 180,
              overflowY: "auto",
              borderRadius: 1,
              px: 1.5,
              py: 1,
              backgroundColor: theme.palette.blushPink.main,
              border: `1px solid ${theme.palette.customColors.borderErrorRedColor}`,
            }}
          >
            <CustomTypography
              variant="heading8"
              fontWeight={700}
              color={theme.palette.customColors.errorRed}
              sx={{ mb: 0.5 }}
            >
              Error history
            </CustomTypography>
            <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
              {errorLines.map((line, index) => (
                <Box component="li" key={`${index}-${line}`} sx={{ mb: 0.5 }}>
                  <CustomTypography
                    variant="body2"
                    color={theme.palette.customColors.errorRed}
                  >
                    {line}
                  </CustomTypography>
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}

        <Grid2 container spacing={1.5} sx={{ mt: 1 }}>
          <Grid2 size={{ xs: 6 }}>
            <AppButton
              label="Cancel"
              variant="grayscale"
              onClick={onClose}
              customStyles={{ width: "100%", minHeight: 40 }}
            />
          </Grid2>
          <Grid2 size={{ xs: 6 }}>
            <AppButton
              label={store.csvUploading ? "Uploading..." : "Upload"}
              variant="primary"
              disabled={!selectedFile || store.csvUploading}
              onClick={handleUpload}
              customStyles={{ width: "100%", minHeight: 40 }}
            />
          </Grid2>
        </Grid2>
      </DialogContent>
    </Dialog>
  );
};

export default observer(VendorPaymentsCsvUploadDialog);
